import Link from 'next/link';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { SignInWallet } from '@web/features/login/SignInWallet';
import { useCurrentCompletedUser } from '@web/features/user/service';
import { Button } from '@web/ui/components';
import { SharpImage } from '@web/ui/components/SharpImage';
import { useDesignsTokenTheme } from '@web/ui/theme/ThemeProvider';

import CtaDarkIllustration from './cta-dark.png';
import CtaLightIllustration from './cta-light.png';

const Container = styled.div`
  overflow: hidden;
  min-height: 30rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: ${({ theme }) => theme.shape.corner['2000']};
  background: ${({ theme }) => theme.color.pink};

  ${({ theme }) => theme.breakpoints.down('lg')} {
    flex-direction: column;
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1 0 0;
  align-self: stretch;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing('3500')};
  gap: ${({ theme }) => theme.spacing('2000')};
`;

const Title = styled.h3`
  white-space: pre;
  text-align: center;
  color: ${({ theme }) => theme.color.onPink};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['10']};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.decreased};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};

  ${({ theme }) => theme.breakpoints.down('lg')} {
    font-size: ${({ theme }) => theme.typescale.size['9']};
  }
`;

const Description = styled.p`
  color: ${({ theme }) => theme.color.onPink};
  text-align: center;
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const VisualContainer = styled.div`
  display: flex;
  align-self: stretch;
  flex: 1 0 0;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  margin-bottom: -5px;
  position: relative;
`;

const Visual = styled(SharpImage)`
  position: absolute;
  bottom: 0;
  left: 0;
  object-fit: cover;
  width: auto;
  min-width: 100%;
  height: 30rem;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    position: static;
    height: 26.5625rem;
  }
`;

export const PersonaJourneyTile = () => {
  const { isDarkMode } = useDesignsTokenTheme();

  const completedUser = useCurrentCompletedUser();
  const t = useTranslations();
  return (
    <Container>
      <Content>
        <Title>{t('CTA_HUB_TILE_TITLE')}</Title>
        <Description>{t('CTA_HUB_TILE_DESCRIPTION')}</Description>
        {completedUser ? (
          <Link href="/program">
            <Button label={t('CTA_HUB_TILE_CONNECTED_BUTTON')} />
          </Link>
        ) : (
          <SignInWallet />
        )}
      </Content>
      <VisualContainer>
        <Visual src={isDarkMode ? CtaDarkIllustration : CtaLightIllustration} />
      </VisualContainer>
    </Container>
  );
};
